/**
 * Simulateur de lois — logique pure (Boîte à outils, 1re → prépa).
 *
 * Niveaux : Monty Hall dès la 1re (probabilités conditionnelles) ; la loi
 * géométrique est au programme de Tle maths complémentaires (« temps
 * d'attente ») et de MP/PT ; MPSI/PTSI restent dans les univers finis ; la loi
 * de Poisson est en MP/PT.
 *
 * Monty Hall à n portes : on choisit une porte, l'animateur (qui sait où est
 * la voiture) ouvre n − 2 portes perdantes ; garder gagne avec probabilité
 * 1/n, changer avec (n − 1)/n. Loi géométrique G(p) : rang du premier succès,
 * P(X = k) = (1 − p)^(k−1) p, P(X > n) = (1 − p)^n, E(X) = 1/p, absence de
 * mémoire P(X > m + n | X > m) = P(X > n). Loi de Poisson P(λ) :
 * P(X = k) = e^(−λ) λ^k / k!, limite de B(n, λ/n) ; E(X) = V(X) = λ.
 *
 * Tout tirage passe par `rng` (dans [0 ; 1[) : `Math.random` dans l'app,
 * `seededRng` dans les tests.
 */

import { formatNumber } from '../../ui/format';

/* ---------- Monty Hall ---------- */

export interface MontyRound {
  doors: number;
  /** porte de la voiture (0…n−1) */
  car: number;
  /** premier choix */
  pick: number;
  /** portes ouvertes par l'animateur (toutes perdantes) */
  opened: number[];
  /** l'autre porte restée fermée : celle qu'on prend en changeant */
  other: number;
}

const randInt = (n: number, rng: () => number) => Math.min(n - 1, Math.floor(rng() * n));

/** l'animateur ouvre n − 2 portes perdantes, jamais celle du joueur */
export function hostOpens(doors: number, car: number, pick: number, rng: () => number): MontyRound {
  const others = Array.from({ length: doors }, (_, i) => i).filter((i) => i !== pick);
  // la porte laissée fermée : la voiture si le joueur s'est trompé, sinon une au hasard
  const other = pick === car ? others[randInt(others.length, rng)]! : car;
  return { doors, car, pick, opened: others.filter((i) => i !== other), other };
}

/** une partie complète : true si la stratégie gagne */
export function playMonty(doors: number, switchDoor: boolean, rng: () => number): boolean {
  const car = randInt(doors, rng);
  const pick = randInt(doors, rng);
  const r = hostOpens(doors, car, pick, rng);
  return (switchDoor ? r.other : r.pick) === car;
}

/** nombre de victoires sur `games` parties */
export function simulateMonty(
  doors: number,
  switchDoor: boolean,
  games: number,
  rng: () => number,
): number {
  let wins = 0;
  for (let i = 0; i < games; i++) if (playMonty(doors, switchDoor, rng)) wins++;
  return wins;
}

/* ---------- loi géométrique ---------- */

export const geomPmf = (p: number, k: number) => (k >= 1 ? (1 - p) ** (k - 1) * p : 0);
/** P(X > n) */
export const geomTail = (p: number, n: number) => (1 - p) ** Math.max(0, n);
export const geomMean = (p: number) => 1 / p;

/** rang du premier succès (plafonné : au-delà, on rend le plafond) */
export function drawGeom(p: number, rng: () => number, cap = 10_000): number {
  let k = 1;
  while (rng() >= p && k < cap) k++;
  return k;
}

/** une série d'épreuves jusqu'au premier succès : false = échec, true = succès */
export function drawSeries(p: number, rng: () => number, cap = 10_000): boolean[] {
  const out: boolean[] = [];
  for (let k = 0; k < cap; k++) {
    const ok = rng() < p;
    out.push(ok);
    if (ok) break;
  }
  return out;
}

/** quantile : plus petit K tel que P(X ≤ K) ≥ q */
export function geomQuantile(p: number, q: number): number {
  return Math.max(1, Math.ceil(Math.log(1 - q) / Math.log(1 - p)));
}

/* ---------- loi de Poisson et binomiale ---------- */

const logFact = (n: number) => {
  let s = 0;
  for (let i = 2; i <= n; i++) s += Math.log(i);
  return s;
};

export const poissonPmf = (lambda: number, k: number) =>
  k < 0 ? 0 : Math.exp(-lambda + k * Math.log(lambda) - logFact(k));

export function binomPmf(n: number, p: number, k: number): number {
  if (k < 0 || k > n) return 0;
  if (p <= 0) return k === 0 ? 1 : 0;
  if (p >= 1) return k === n ? 1 : 0;
  return Math.exp(
    logFact(n) - logFact(k) - logFact(n - k) + k * Math.log(p) + (n - k) * Math.log(1 - p),
  );
}

/** écart maximal entre B(n, λ/n) et P(λ) sur k = 0…K */
export function maxGap(lambda: number, n: number, K: number): number {
  let g = 0;
  for (let k = 0; k <= K; k++)
    g = Math.max(g, Math.abs(binomPmf(n, lambda / n, k) - poissonPmf(lambda, k)));
  return g;
}

/** dernier k affiché pour P(λ) : la masse au-delà est négligeable */
export const poissonMaxK = (lambda: number) =>
  Math.min(30, Math.ceil(lambda + 4 * Math.sqrt(lambda) + 2));

/* ---------- échantillon ---------- */

export interface Sample {
  /** effectifs par valeur (indice = valeur) */
  counts: number[];
  total: number;
  sum: number;
}

export const emptySample = (): Sample => ({ counts: [], total: 0, sum: 0 });

export function addValues(s: Sample, values: readonly number[]): Sample {
  const counts = [...s.counts];
  let sum = s.sum;
  for (const v of values) {
    counts[v] = (counts[v] ?? 0) + 1;
    sum += v;
  }
  for (let i = 0; i < counts.length; i++) counts[i] ??= 0;
  return { counts, total: s.total + values.length, sum };
}

export const freq = (s: Sample, v: number) => (s.total ? (s.counts[v] ?? 0) / s.total : 0);
/** effectif des valeurs > n */
export const countAbove = (s: Sample, n: number) =>
  s.counts.reduce((acc, c, v) => (v > n ? acc + c : acc), 0);

/* ---------- écriture ---------- */

const NNBSP = String.fromCharCode(0x202f);
/** « 0,128 », « 2/3 » */
export const dec = (v: number, digits = 4) => formatNumber(v, digits);
/** « = 0,25 » si l'arrondi est exact, sinon « ≈ 6,67 » */
export const eqText = (v: number, digits = 4) =>
  `${Math.abs(Number(v.toFixed(digits)) - v) < 1e-12 ? '=' : '≈'} ${dec(v, digits)}`;
/** entier groupé par milliers, espace fine insécable : « 1 000 » */
export const count = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, NNBSP);
export const fracText = (a: number, b: number) => `${a}/${b}`;
const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
/** exposant : « ² », « ¹⁰ » */
export const sup = (n: number) =>
  String(n)
    .split('')
    .map((c) => SUP[Number(c)] ?? c)
    .join('');

/* ---------- défi ---------- */

export type QuizKind =
  | 'monty'
  | 'montyKeep'
  | 'montyHost'
  | 'montyIgnorant'
  | 'montyGames'
  | 'geomE'
  | 'geomP'
  | 'geomTail'
  | 'memory'
  | 'poisson0'
  | 'poissonE';

export interface QuizOption {
  text: string;
  correct: boolean;
  hint?: string;
}

export interface Quiz {
  kind: QuizKind;
  question: string;
  options: QuizOption[];
  explain: string;
}

/** thème d'un défi : Monty Hall seul, loi géométrique seule, ou tout (MP/PT) */
export type QuizTheme = 'monty' | 'geom' | 'all';

export const THEME_KINDS: Record<QuizTheme, QuizKind[]> = {
  // cinq questions différentes ; garder / changer séparées
  monty: ['monty', 'montyHost', 'montyIgnorant', 'montyKeep', 'montyGames'],
  geom: ['geomE', 'geomP', 'geomTail', 'memory'],
  all: ['monty', 'geomE', 'memory', 'poisson0', 'poissonE'],
};

export const THEME_LABELS: Record<QuizTheme, string> = {
  monty: 'Monty Hall',
  geom: 'Loi géométrique',
  all: 'Tout',
};

/**
 * Thèmes du défi selon le niveau (rang : 1re = 6, Tle = 7, MPSI = 8, MP = 9),
 * le premier par défaut. Tle : géométrique (maths complémentaires) d'abord ;
 * MPSI/PTSI : univers finis, Monty Hall seul.
 */
export function quizThemes(rank: number | null): QuizTheme[] {
  if (rank === 7) return ['geom', 'monty'];
  if (rank === 9 || rank === null) return ['all', 'monty', 'geom'];
  return ['monty'];
}

const pick = <T>(xs: readonly T[], rnd: () => number): T => xs[Math.floor(rnd() * xs.length)]!;

function shuffle<T>(xs: readonly T[], rnd: () => number): T[] {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/**
 * Options distinctes PAR VALEUR (la bonne d'abord, gardée), quatre au plus,
 * mélangées. Chaque mauvaise : [texte, valeur, indice].
 */
function options(
  good: string,
  goodValue: number,
  wrongs: ReadonlyArray<readonly [string, number, string]>,
  rnd: () => number,
) {
  const out: QuizOption[] = [{ text: good, correct: true }];
  const values = [goodValue];
  for (const [text, v, hint] of wrongs) {
    if (values.some((x) => Math.abs(x - v) < 1e-9) || out.some((o) => o.text === text)) continue;
    values.push(v);
    out.push({ text, correct: false, hint });
  }
  return shuffle(out.slice(0, 4), rnd);
}

/** p des questions en puissances : jamais 0,5 (p = 1 − p confondrait les formules) */
const PS = [0.1, 0.2, 0.3, 0.4] as const;
/** « 0,8³ » (« 0,8 » à la puissance 1) */
const pw = (b: number, e: number) => (e === 1 ? dec(b) : `${dec(b)}${sup(e)}`);
const plural = (n: number, w: string) => `${n} ${w}${n > 1 ? 's' : ''}`;

export function makeQuiz(kind: QuizKind, rnd: () => number): Quiz {
  switch (kind) {
    case 'monty': {
      const n = pick([3, 4, 5, 6, 10], rnd);
      return {
        kind,
        question: `Monty Hall à ${n} portes : tu choisis une porte, l’animateur (qui sait où est la voiture) ouvre ${plural(n - 2, 'porte')} perdante${n - 2 > 1 ? 's' : ''} parmi les autres. Quelle est la probabilité de gagner en changeant pour la porte restée fermée ?`,
        options: options(
          fracText(n - 1, n),
          (n - 1) / n,
          [
            [
              '1/2',
              1 / 2,
              'Il reste deux portes, mais elles ne sont pas équiprobables : l’animateur sait où est la voiture.',
            ],
            [
              fracText(1, n),
              1 / n,
              `C’est la probabilité de gagner en gardant : le premier choix est bon une fois sur ${n}.`,
            ],
            [
              fracText(1, n - 1),
              1 / (n - 1),
              'L’animateur n’ouvre jamais la porte de la voiture : changer gagne dès que le premier choix était perdant.',
            ],
            [
              '1',
              1,
              'Changer ne gagne pas à coup sûr : si le premier choix était le bon, changer fait perdre.',
            ],
          ],
          rnd,
        ),
        explain: `En changeant, on gagne exactement quand le premier choix était perdant : probabilité ${fracText(n - 1, n)}.`,
      };
    }
    case 'montyKeep': {
      const n = pick([4, 5, 6], rnd);
      return {
        kind,
        question: `Monty Hall à ${n} portes. Quelle est la probabilité de gagner en GARDANT sa porte, après que l’animateur a ouvert ${plural(n - 2, 'porte')} perdante${n - 2 > 1 ? 's' : ''} ?`,
        options: options(
          fracText(1, n),
          1 / n,
          [
            [
              '1/2',
              1 / 2,
              'Ouvrir des portes perdantes ne change pas la chance du premier choix : l’animateur peut toujours le faire.',
            ],
            [fracText(n - 1, n), (n - 1) / n, 'C’est la probabilité de gagner en changeant.'],
            [
              fracText(1, n - 1),
              1 / (n - 1),
              `Le premier choix se fait parmi ${n} portes, avant toute ouverture.`,
            ],
          ],
          rnd,
        ),
        explain: `On garde la porte choisie au hasard parmi ${n} : on gagne avec probabilité ${fracText(1, n)}, quoi que fasse l’animateur.`,
      };
    }
    case 'montyHost': {
      // 3 portes, le joueur prend la 1 : l'animateur ouvre la 3 avec quelle probabilité ?
      const atMine = rnd() < 0.5;
      const car = atMine ? 1 : 2;
      const never =
        'Il choisit parmi les portes qu’il peut ouvrir, jamais la tienne : pas 1 chance sur 3.';
      return {
        kind,
        question: `Monty Hall à 3 portes. Tu choisis la porte 1, et la voiture est derrière la porte ${car}. Avec quelle probabilité l’animateur ouvre-t-il la porte 3 ?`,
        options: atMine
          ? options(
              '1/2',
              1 / 2,
              [
                [
                  '1',
                  1,
                  'Les portes 2 et 3 cachent toutes deux une chèvre : il en choisit une au hasard.',
                ],
                ['1/3', 1 / 3, never],
                ['0', 0, 'Il peut ouvrir la porte 3 : elle cache une chèvre.'],
              ],
              rnd,
            )
          : options(
              '1',
              1,
              [
                [
                  '1/2',
                  1 / 2,
                  'Il n’a pas le choix : il ne peut ouvrir ni ta porte ni celle de la voiture.',
                ],
                ['1/3', 1 / 3, never],
                ['0', 0, 'La porte 3 cache une chèvre, et c’est la seule qu’il peut ouvrir.'],
              ],
              rnd,
            ),
        explain: atMine
          ? 'Il ne peut pas ouvrir ta porte et les deux autres cachent des chèvres : il choisit au hasard, probabilité 1/2. Avec la formule de Bayes, c’est ce qui donne P(voiture en 2 | il ouvre la 3) = 2/3.'
          : 'Il ne peut ouvrir ni la porte 1 (la tienne) ni la porte 2 (la voiture) : il ouvre la porte 3 à coup sûr, probabilité 1. Avec la formule de Bayes, c’est ce qui donne P(voiture en 2 | il ouvre la 3) = 2/3.',
      };
    }
    case 'montyIgnorant': {
      const n = pick([3, 4, 5], rnd);
      return {
        kind,
        question: `Monty Hall à ${n} portes, mais l’animateur NE SAIT PAS où est la voiture : il ouvre au hasard ${plural(n - 2, 'porte')} parmi les autres et, par chance, ce ne sont que des chèvres. Quelle est alors la probabilité de gagner en changeant ?`,
        options: options(
          '1/2',
          1 / 2,
          [
            [
              fracText(n - 1, n),
              (n - 1) / n,
              'C’est vrai quand l’animateur sait. Ici, il aurait pu montrer la voiture : ne voir que des chèvres est une information qui compte.',
            ],
            [
              fracText(1, n),
              1 / n,
              'C’est la probabilité que le premier choix soit bon AVANT toute ouverture ; on sait maintenant que les portes ouvertes cachaient des chèvres.',
            ],
            [
              fracText(1, n - 1),
              1 / (n - 1),
              'Il ne reste que deux portes fermées : la question est de savoir si elles se valent.',
            ],
            ['1', 1, 'Changer ne gagne pas à coup sûr : le premier choix pouvait être le bon.'],
          ],
          rnd,
        ),
        explain: `On ne voit que des chèvres dans deux cas : le premier choix était le bon (probabilité ${fracText(1, n)}), ou il était faux et la voiture est derrière la porte restée fermée (${fracText(n - 1, n)} × ${fracText(1, n - 1)} = ${fracText(1, n)}). Ces deux cas ont la même probabilité : 1/2 chacun.`,
      };
    }
    case 'montyGames': {
      const n = pick([3, 4, 5, 10], rnd);
      const N = n === 10 ? 1000 : n * pick([100, 200], rnd);
      const good = (N * (n - 1)) / n;
      return {
        kind,
        question: `On joue ${count(N)} parties de Monty Hall à ${n} portes en changeant toujours de porte. Combien de victoires en moyenne ?`,
        options: options(
          count(good),
          good,
          [
            [
              count(N / 2),
              N / 2,
              'Les deux portes restées fermées ne se valent pas : changer gagne plus d’une fois sur deux.',
            ],
            [
              count(N / n),
              N / n,
              'C’est en gardant sa porte : le premier choix est bon une fois sur ' + n + '.',
            ],
            [count(N), N, 'Si le premier choix était le bon, changer fait perdre.'],
          ],
          rnd,
        ),
        explain: `Chaque partie est gagnée avec probabilité ${fracText(n - 1, n)} : en moyenne ${count(N)} × ${fracText(n - 1, n)} = ${count(good)} victoires (espérance d’une loi binomiale).`,
      };
    }
    case 'geomE': {
      const p = pick([0.1, 0.2, 0.25, 0.5], rnd);
      const e = 1 / p;
      return {
        kind,
        question: `X suit la loi géométrique de paramètre p = ${dec(p)} (rang du premier succès). Que vaut E(X) ?`,
        options: options(
          dec(e),
          e,
          [
            [
              dec(p),
              p,
              'C’est p. Plus le succès est rare, plus on l’attend longtemps : E(X) = 1/p.',
            ],
            [
              dec(e - 1),
              e - 1,
              'C’est le nombre moyen d’échecs avant le succès : E(X) = 1/p compte aussi l’épreuve du succès.',
            ],
            [dec(1 - p), 1 - p, 'C’est la probabilité d’un échec, 1 − p.'],
            [
              dec(e + 1),
              e + 1,
              'Une épreuve de trop : l’épreuve du succès compte, mais une seule fois. E(X) = 1/p.',
            ],
          ],
          rnd,
        ),
        explain: `E(X) = 1/p = 1/${dec(p)} = ${dec(e)} : en moyenne, le premier succès arrive à la ${dec(e)}e épreuve.`,
      };
    }
    case 'geomP': {
      const p = pick(PS, rnd);
      const q = 1 - p;
      const k = pick([2, 3, 4], rnd);
      const good = geomPmf(p, k);
      return {
        kind,
        question: `X suit la loi géométrique de paramètre p = ${dec(p)}. Que vaut P(X = ${k}) ?`,
        options: options(
          `${pw(q, k - 1)} × ${dec(p)}`,
          good,
          [
            [
              `${pw(q, k)} × ${dec(p)}`,
              q ** k * p,
              `Il y a ${plural(k - 1, 'échec')} puis le succès : (1 − p)${sup(k - 1)} × p, pas (1 − p)${sup(k)} × p.`,
            ],
            [
              pw(p, k),
              p ** k,
              `${k} succès de suite, c’est p${sup(k)}. Ici : ${plural(k - 1, 'échec')}, puis un succès.`,
            ],
            [
              pw(q, k - 1),
              q ** (k - 1),
              `C’est P(X > ${k - 1}) : il faut encore le succès à la ${k}e épreuve (× p).`,
            ],
            [`1 − ${pw(q, k)}`, 1 - q ** k, `C’est P(X ≤ ${k}).`],
          ],
          rnd,
        ),
        explain: `X = ${k} : ${plural(k - 1, 'échec')} puis un succès, épreuves indépendantes : P(X = ${k}) = (1 − p)${sup(k - 1)} × p = ${pw(q, k - 1)} × ${dec(p)} ${eqText(good)}.`,
      };
    }
    case 'geomTail': {
      const p = pick(PS, rnd);
      const q = 1 - p;
      const n = pick([2, 3, 4], rnd);
      const good = geomTail(p, n);
      return {
        kind,
        question: `X suit la loi géométrique de paramètre p = ${dec(p)}. Que vaut P(X > ${n}) ?`,
        options: options(
          pw(q, n),
          good,
          [
            [`1 − ${pw(q, n)}`, 1 - good, `C’est P(X ≤ ${n}).`],
            [
              pw(q, n - 1),
              q ** (n - 1),
              `X > ${n} : les ${n} premières épreuves sont des échecs, (1 − p)${sup(n)}.`,
            ],
            [pw(p, n), p ** n, `X > ${n} veut dire ${n} échecs, pas ${n} succès.`],
          ],
          rnd,
        ),
        explain: `X > ${n} : les ${n} premières épreuves sont des échecs, donc P(X > ${n}) = (1 − p)${sup(n)} = ${pw(q, n)} ${eqText(good)}.`,
      };
    }
    case 'memory': {
      const p = pick(PS, rnd);
      const q = 1 - p;
      const m = pick([4, 5, 10], rnd);
      const n = pick([2, 3], rnd);
      const good = geomTail(p, n);
      return {
        kind,
        question: `X suit la loi géométrique de paramètre p = ${dec(p)}. Sachant X > ${m}, quelle est la probabilité que X > ${m + n} ?`,
        options: options(
          pw(q, n),
          good,
          [
            [
              pw(q, m + n),
              geomTail(p, m + n),
              `C’est P(X > ${m + n}) sans la condition : sachant X > ${m}, tout recommence.`,
            ],
            [pw(q, m), geomTail(p, m), `C’est P(X > ${m}).`],
            [`1 − ${pw(q, n)}`, 1 - good, `C’est P(X ≤ ${n}).`],
          ],
          rnd,
        ),
        explain: `Absence de mémoire : P(X > ${m + n} | X > ${m}) = (1 − p)${sup(m + n)} / (1 − p)${sup(m)} = (1 − p)${sup(n)} = P(X > ${n}) = ${pw(q, n)} ${eqText(good)}.`,
      };
    }
    case 'poisson0': {
      const lambda = pick([0.5, 2, 3, 4], rnd);
      const good = poissonPmf(lambda, 0);
      return {
        kind,
        question: `X suit la loi de Poisson de paramètre λ = ${dec(lambda)}. Que vaut P(X = 0), à 10⁻⁴ près ?`,
        options: options(
          `≈ ${dec(good)}`,
          good,
          [
            [
              `≈ ${dec(poissonPmf(lambda, 1))}`,
              poissonPmf(lambda, 1),
              'C’est P(X = 1) = λ e^(−λ).',
            ],
            [`≈ ${dec(1 - good)}`, 1 - good, 'C’est P(X ≥ 1).'],
            [
              `≈ ${dec(Math.exp(-1 / lambda))}`,
              Math.exp(-1 / lambda),
              'P(X = 0) = e^(−λ) : λ en exposant, pas 1/λ.',
            ],
          ],
          rnd,
        ),
        explain: `P(X = 0) = e^(−λ) λ⁰ / 0! = e^(−${dec(lambda)}) ≈ ${dec(good)}.`,
      };
    }
    case 'poissonE': {
      const lambda = pick([2, 3, 5], rnd);
      const l = dec(lambda);
      return {
        kind,
        question: `X suit la loi de Poisson de paramètre λ = ${l}. Que valent E(X) et V(X) ?`,
        options: options(
          `E(X) = ${l} et V(X) = ${l}`,
          0,
          [
            [
              `E(X) = ${l} et V(X) = ${dec(lambda * lambda)}`,
              1,
              'Pour une loi de Poisson, la variance est aussi λ, pas λ².',
            ],
            [`E(X) = ${l} et V(X) = √${l}`, 2, 'C’est l’écart-type σ = √λ ; la variance vaut λ.'],
            [
              `E(X) = √${l} et V(X) = ${l}`,
              3,
              'E(X) = λ : en moyenne λ événements. C’est l’écart-type qui vaut √λ.',
            ],
          ],
          rnd,
        ),
        explain: `Loi de Poisson : E(X) = V(X) = λ = ${l}. C’est la limite de B(n ; λ/n) : np = λ et np(1 − λ/n) → λ.`,
      };
    }
  }
}
